import { z } from "zod";
import { zId } from "./common.js";

export const IMPORT_SOURCES = [
  "generic_csv",
  "raisers_edge",
  "bloomerang",
  "virtuous",
  "donorperfect",
  "little_green_light",
] as const;
export const zImportSource = z.enum(IMPORT_SOURCES);

/** CT-RM-34: "custom_objects" imports records of one custom object type (mapping.objectType) */
export const IMPORT_TARGETS = ["constituents", "gifts", "custom_objects"] as const;
export const zImportTarget = z.enum(IMPORT_TARGETS);

export const zImportMapping = z
  .object({
    /** CSV column header -> CauseTide field path (e.g. "firstName", "email", "custom.my_key") */
    columns: z.record(z.string()),
    source: zImportSource.default("generic_csv"),
    target: zImportTarget,
    dedupeMode: z.enum(["skip", "merge", "create_anyway"]).default("skip"),
    /** gift imports: fund used when a row has no fund column value */
    defaultFundId: zId.optional(),
    /** custom object imports: the type's key */
    objectType: z.string().regex(/^[a-z][a-z0-9_]*$/).max(40).optional(),
  })
  .refine((m) => m.target !== "custom_objects" || Boolean(m.objectType), { message: "Custom object imports need objectType", path: ["objectType"] });

/**
 * Importable fields for a custom object type, besides `custom.<key>` for each
 * of its fields: the record name, and an optional constituent to link (found
 * like a gift's donor) with a role.
 */
export const IMPORT_CUSTOM_OBJECT_FIELDS = ["name", "constituentSourceId", "constituentEmail", "constituentName", "role"] as const;
export type ImportMapping = z.infer<typeof zImportMapping>;
export const IMPORT_DEDUPE_MODES = ["skip", "merge", "create_anyway"] as const;

// ---------------------------------------------------------------------------
// saved mapping templates (CT-RM-15)
// ---------------------------------------------------------------------------

export const zImportTemplate = z.object({
  id: zId,
  name: z.string(),
  target: zImportTarget,
  source: zImportSource,
  columns: z.record(z.string()),
  /** every header of the file the template was saved from — used for matching */
  headers: z.array(z.string()),
  dedupeMode: z.enum(IMPORT_DEDUPE_MODES),
  defaultFundId: zId.nullable(),
  createdById: zId,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ImportTemplate = z.infer<typeof zImportTemplate>;

export const zCreateImportTemplate = z.object({
  name: z.string().trim().min(1).max(120),
  target: zImportTarget,
  source: zImportSource.default("generic_csv"),
  columns: z.record(z.string()),
  headers: z.array(z.string()).max(500).optional(),
  dedupeMode: z.enum(IMPORT_DEDUPE_MODES).default("skip"),
  defaultFundId: zId.nullable().optional(),
});
export type CreateImportTemplate = z.infer<typeof zCreateImportTemplate>;

export const zUpdateImportTemplate = zCreateImportTemplate
  .omit({ target: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type UpdateImportTemplate = z.infer<typeof zUpdateImportTemplate>;

/** A saved template whose headers overlap the uploaded file. score is 0–100. */
export const zImportTemplateMatch = zImportTemplate.extend({ score: z.number().int().min(0).max(100) });
export type ImportTemplateMatch = z.infer<typeof zImportTemplateMatch>;

/** Templates scoring at or above this are suggested on upload. */
export const TEMPLATE_MATCH_THRESHOLD = 50;

const normHeader = (h: string) => h.trim().toLowerCase().replace(/\s+/g, " ");

/** Header-set similarity (Jaccard on normalized headers), 0–100. */
export function headerMatchScore(fileHeaders: string[], templateHeaders: string[]): number {
  const a = new Set(fileHeaders.map(normHeader).filter(Boolean));
  const b = new Set(templateHeaders.map(normHeader).filter(Boolean));
  if (!a.size || !b.size) return 0;
  let shared = 0;
  for (const h of a) if (b.has(h)) shared++;
  return Math.round((100 * shared) / (a.size + b.size - shared));
}

/** Re-keys a template's columns onto the file's actual header spelling; unmatched columns are dropped. */
export function applyTemplateColumns(fileHeaders: string[], columns: Record<string, string>): Record<string, string> {
  const byNorm = new Map(Object.entries(columns).map(([h, f]) => [normHeader(h), f]));
  const out: Record<string, string> = {};
  for (const h of fileHeaders) {
    const field = byNorm.get(normHeader(h));
    if (field) out[h] = field;
  }
  return out;
}

// ---------------------------------------------------------------------------
// merge from preview (CT-RM-15)
// ---------------------------------------------------------------------------

/** Single-value fields compared side by side (map to MergeService scalar choices). */
export const IMPORT_MERGE_SCALAR_FIELDS = [
  "prefix",
  "firstName",
  "middleName",
  "lastName",
  "suffix",
  "nickname",
  "orgName",
  "birthDate",
] as const;
/** Multi-value fields: emails (email+email2), phones (phone+phone2), address. */
export const IMPORT_MERGE_MULTI_FIELDS = ["emails", "phones", "address"] as const;
export const IMPORT_MERGE_FIELDS = [...IMPORT_MERGE_SCALAR_FIELDS, ...IMPORT_MERGE_MULTI_FIELDS] as const;
export type ImportMergeField = (typeof IMPORT_MERGE_FIELDS)[number];

/** existing = keep the record's value; incoming = use the file's value (replaces for multi-value); both = add alongside (multi-value only). */
export const zImportFieldChoice = z.enum(["existing", "incoming", "both"]);
export type ImportFieldChoice = z.infer<typeof zImportFieldChoice>;

export const zImportRowResolution = z.discriminatedUnion("action", [
  z.object({ action: z.literal("create") }),
  z.object({ action: z.literal("skip") }),
  z.object({
    action: z.literal("merge"),
    fields: z.record(z.enum(IMPORT_MERGE_FIELDS), zImportFieldChoice).default({}),
  }),
]);
export type ImportRowResolution = z.infer<typeof zImportRowResolution>;

/** row number (as in the duplicates list) -> resolution */
export const zImportResolutions = z.object({
  resolutions: z.record(z.string().regex(/^\d+$/), zImportRowResolution),
});
export type ImportResolutions = z.infer<typeof zImportResolutions>;

/** Duplicate scores at or above this count as high confidence (email match). */
export const HIGH_CONFIDENCE_DUPLICATE = 90;

export interface ImportCompareValues {
  prefix: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  nickname: string | null;
  orgName: string | null;
  birthDate: string | null;
  emails: string[];
  phones: string[];
  address: string | null;
}

export interface ImportDuplicateCompare {
  row: number;
  score: number;
  highConfidence: boolean;
  incoming: ImportCompareValues;
  existing: ({ id: string; displayName: string } & ImportCompareValues) | null;
  /** fields that differ between incoming and existing (incoming non-empty) */
  differing: ImportMergeField[];
  /** sensible defaults: fill gaps from incoming, keep existing on conflicts, add both for contact info */
  suggested: Partial<Record<ImportMergeField, ImportFieldChoice>>;
  resolution: ImportRowResolution | null;
}

export const zImportBatch = z.object({
  id: zId,
  fileName: z.string(),
  target: zImportTarget,
  source: zImportSource,
  status: z.enum(["pending", "validating", "validated", "running", "completed", "failed", "rolled_back"]),
  totalRows: z.number().int(),
  processedRows: z.number().int(),
  createdCount: z.number().int(),
  updatedCount: z.number().int(),
  skippedCount: z.number().int(),
  errorCount: z.number().int(),
  errors: z.array(z.object({ row: z.number(), message: z.string() })).default([]),
  duplicates: z
    .array(z.object({ row: z.number(), name: z.string(), matchName: z.string(), matchId: zId, score: z.number() }))
    .default([]),
  mapping: zImportMapping.nullable(),
  resolutions: z.record(zImportRowResolution).default({}),
  mergedCount: z.number().int().default(0),
  createdById: zId,
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});
export type ImportBatch = z.infer<typeof zImportBatch>;

/** Importable constituent fields for the mapping UI */
export const IMPORT_CONSTITUENT_FIELDS = [
  "type",
  "prefix",
  "firstName",
  "middleName",
  "lastName",
  "suffix",
  "nickname",
  "orgName",
  "email",
  "email2",
  "phone",
  "phone2",
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "postalCode",
  "country",
  "birthDate",
  "tags",
  "sourceId",
] as const;

export const IMPORT_GIFT_FIELDS = [
  "constituentSourceId",
  "constituentEmail",
  "constituentName",
  "amount",
  "date",
  "fund",
  "campaign",
  "appeal",
  "paymentMethod",
  "channel",
  "checkNumber",
  "notes",
  "sourceId",
] as const;
